import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/atoms/NewPcButton';
import SearchIcon from '@/assets/icons/Monitoring/SearchIcon.svg?react';
import { LayoutStickySection } from '../orders/_layouts/LayoutOrderContainer';
import { Dialog, DialogContent, DialogClose } from '@/components/atoms/Dialog';
import { usePost, CPLAT_API_ROUTES } from '@/api';
import { useToast } from '@/hooks/V3/useToast';
import { useAppSelector } from '@/redux/store';
import { cplatApiV2 } from '@/api';
import SubmitMonitoring from './SubmitMonitoring';
import { useMonitoringGoods } from './MonitoringContext';
import { useLog } from '@/hooks/V3/useLog';
import { useRecoilValue } from 'recoil';
import { deviceUuidState } from '@/store/atom';

export const SearchBar = () => {
  const [url, setUrl] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchResult, setSearchResult] = useState<any>(null);
  const [relatedList, setRelatedList] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const { showToast } = useToast();
  const user = useAppSelector((state) => state.user);
  const { refetch } = useMonitoringGoods();
  // 등록 mutation
  const { mutate: addMonitoringGoods, isPending: isAdding } = usePost(
    CPLAT_API_ROUTES.setUserMonitoringGoods,
  );

  const { setUserEventHistory } = useLog(user.UserNo);
  const deviceUuid = useRecoilValue(deviceUuidState);

  // 검색 버튼 클릭 핸들러 (cplatApiV2 사용)
  const handleSearch = async () => {
    if (!url.trim()) {
      showToast('warning', 'URL을 입력해주세요.');
      return;
    }

    setUserEventHistory({
      eventName: 'monitoring_product_search_web_dashboard',
      eventValue: url.trim(),
      deviceUuid,
    });

    setIsSearching(true);
    try {
      const res = await cplatApiV2.post(CPLAT_API_ROUTES.searchGoodsProductUrl, {
        cplatToken: user.CplatToken,
        GoodsUrl: url,
      });
      const data = res.data;
      const result = Array.isArray(data.data) ? data.data[0] : data.data;
      if (!result || data.code !== '200') {
        showToast('warning', data.message || '상품을 찾을 수 없습니다.');
        setSearchResult(null);
        setDialogOpen(false);
      } else {
        setSearchResult(result);
        setDialogOpen(true);
      }
    } catch (error: any) {
      // Sentry는 interceptor에서 자동 처리됨
      showToast('warning', error?.message || '상품 검색 중 오류가 발생했습니다.');
      setSearchResult(null);
      setDialogOpen(false);
    } finally {
      setIsSearching(false);
    }
  };

  // 등록 버튼 클릭 핸들러 (react-query mutation)
  const handleRegister = () => {
    if (!searchResult) return;
    addMonitoringGoods(
      {
        cplatToken: user.CplatToken,
        StoreName: searchResult.StoreName,
        GoodsNo: searchResult.GoodsNo,
        GoodsImageUrl: searchResult.GoodsImageUrl,
        GoodsName: searchResult.GoodsName,
        CategoryFullName: searchResult.CategoryFullName,
        UseYn: 'Y',
        RelatedKeywordList: relatedList,
        GoodsUrl: searchResult.GoodsUrl,
        GoodsPrice: searchResult.GoodsPrice,
        relatedKeywordRankInfo: searchResult.RelatedKeywordRankingList?.[0],
        ShippingFee: searchResult.ShippingFee,
        GoodsRegisterDate: searchResult.GoodsRegisterDate,
        NoticeYn: 'N',
        IsMyStoreGoods: searchResult.IsMyStoreGoods,
        MallSeq: searchResult.MallSeq,
        InsertYn: 'Y',
      },
      {
        onSuccess: () => {
          showToast('success', '모니터링 상품이 추가되었습니다!');
          setDialogOpen(false);
          setUrl('');
          setSearchResult(null);
          setRelatedList([]);

          refetch(); // 모니터링 상품 목록 새로고침
        },
        onError: () => {
          showToast('warning', '상품 등록에 실패했습니다.');
        },
      },
    );
  };
  const MAX_KEYWORDS = 10;

  const handleToggleKeyword = (keyword: string) => {
    if (relatedList.includes(keyword)) {
      setRelatedList(relatedList.filter((k) => k !== keyword));
    } else if (relatedList.length < MAX_KEYWORDS) {
      setRelatedList([...relatedList, keyword]);
    } else {
      showToast('warning', `최대 ${MAX_KEYWORDS}개까지 선택할 수 있습니다.`);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <LayoutStickySection className="max-w-full border-none bg-gr-50/90 px-0 py-[10px]">
        <div className="flex max-w-full rounded-[16px] border border-gr-300 bg-white px-[12px] py-[10px]  focus-within:border-point-400">
          <input
            type="text"
            placeholder="모니터링할 상품의 Url을 입력해주세요"
            className="h-full flex-1 pr-[10px] text-gr-800 Body5M14 focus:outline-none"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            disabled={isSearching}
          />
          <Button
            className="h-[34px] min-w-[67px] gap-[6px] px-[10px] py-[6px] Body5M14"
            onClick={handleSearch}
            isLoading={isSearching}
            disabled={isSearching}
          >
            <SearchIcon />
            검색
          </Button>
        </div>
      </LayoutStickySection>
      <SubmitMonitoring
        isSearching={isSearching}
        handleSearch={handleSearch}
        searchResult={searchResult}
        setSearchResult={setSearchResult}
        setDialogOpen={setDialogOpen}
        setUrl={setUrl}
        url={url}
      />
    </Dialog>
  );
};
